import { createFileRoute, Link } from "@tanstack/react-router";
import craftImg from "@/assets/craft-hands.jpg";
import heroImg from "@/assets/hero-jacket.jpg";
import { BackButton } from "@/components/site/BackButton";

export const Route = createFileRoute("/about")({
  head: () => ({
    meta: [
      { title: "The Atelier — KAKAIYS" },
      {
        name: "description",
        content:
          "KAKAIYS is a quiet house of leather. Discover our atelier, our craft, and the people who make every piece by hand.",
      },
      { property: "og:title", content: "The Atelier — KAKAIYS" },
      {
        property: "og:description",
        content: "Inside the KAKAIYS atelier — where every piece is made by hand.",
      },
      { property: "og:image", content: craftImg },
      { property: "og:url", content: "https://kakaiys.com/about" },
    ],
      links: [{ rel: "canonical", href: "https://kakaiys.com/about" }],
  }),
  component: About,
});

function About() {
  return (
    <>
      <section className="pt-40 pb-20 container-luxe text-center">
        <BackButton fallback="/" className="mb-8" />
        <div className="eyebrow mb-5">The Maison</div>
        <h1 className="font-serif text-5xl md:text-8xl leading-[0.95]">
          Made slowly,
          <br />
          <span className="italic">by one pair of hands.</span>
        </h1>
      </section>

      <section className="relative aspect-[16/9] md:aspect-[21/9] overflow-hidden">
        <img
          src={craftImg}
          alt="Master craftsman stitching leather"
          width={1920}
          height={1080}
          className="h-full w-full object-cover"
        />
      </section>

      <section className="container-luxe py-24 md:py-32 grid md:grid-cols-12 gap-10">
        <div className="md:col-span-4">
          <div className="eyebrow mb-3">Origins</div>
          <h2 className="font-serif text-3xl md:text-4xl">A house built on patience.</h2>
        </div>
        <div className="md:col-span-7 md:col-start-6 space-y-6 text-foreground/80 leading-relaxed">
          <p>
            KAKAIYS was founded in Sialkot — a city whose leather has dressed the world for over a
            century — with a simple intent: to make the kind of objects that get better with time
            and harder to part with.
          </p>
          <p>
            We chose the harder road. We do not stamp. We do not glue. Every seam is sewn by a
            single artisan, every edge burnished by hand. A jacket takes fourteen hours. A
            handbag, twenty-two. We never rush.
          </p>
          <p>
            What we offer is not fashion. It is a small collection of considered pieces, made one
            at a time, intended to outlive the trends that surround them.
          </p>
        </div>
      </section>

      <section className="container-luxe pb-24 md:pb-32">
        <div className="grid md:grid-cols-3 gap-12 md:gap-16">
          {[
            { n: "01", t: "The Hide", d: "We source full-grain leather from the finest tanneries in Tuscany and Sialkot." },
            { n: "02", t: "The Hand", d: "Every piece is cut, stitched and finished by a single named artisan." },
            { n: "03", t: "The Time", d: "We refuse to rush. Each piece takes the hours it needs — not a minute less." },
          ].map((b) => (
            <div key={b.n}>
              <div className="font-serif text-5xl text-muted-foreground">{b.n}</div>
              <h3 className="font-serif text-2xl mt-4">{b.t}</h3>
              <p className="mt-3 text-sm text-muted-foreground leading-relaxed">{b.d}</p>
            </div>
          ))}
        </div>
      </section>

      <section className="relative h-[70vh] min-h-[480px] overflow-hidden bg-foreground">
        <img
          src={heroImg}
          alt="Black leather jacket on marble"
          width={1920}
          height={1080}
          className="h-full w-full object-cover opacity-80 ken-burns"
        />
        <div className="absolute inset-0 flex items-center justify-center text-center text-background px-6">
          <div className="max-w-xl">
            <div className="eyebrow text-background/70 mb-4">An Invitation</div>
            <h2 className="font-serif text-4xl md:text-6xl leading-tight">
              Begin your collection.
            </h2>
            <Link
              to="/shop"
              className="mt-10 inline-flex items-center justify-center bg-background px-10 py-4 text-[11px] uppercase tracking-[0.28em] text-foreground hover:bg-background/90"
            >
              Discover the Collection
            </Link>
          </div>
        </div>
      </section>
    </>
  );
}
